// Span-based de-duplication of recurring video-analysis detections.
//
// The analysis AI emits a detection for the same physical damage across many
// frames (and sometimes several RoI regions for one damage), so the same code
// shows up repeatedly. This collapses, per main code, detections whose distance
// spans [beginDistance, endDistance] overlap or sit within `gap` metres into a
// single detection, keeping the highest-accuracy member as the representative
// (its peak time/metre/characterizations drive the row, arrow and video seek).
//
// When there is no distance data (time-based analyses, or begin==end==0) each
// detection degrades to a point interval at `time`, reproducing the previous
// peak-proximity merge in seconds.

export interface MergeableDetection {
  mainCode: string;
  timestamp: number;
  meters: number;
  accuracy: number;
  beginDistance?: number;
  endDistance?: number;
  color?: string;
  c1?: string;
  c2?: string;
  // Pass-through identity (e.g. row id) preserved on the representative.
  id?: string;
}

export interface MergeOptions {
  // Max gap (metres when byMetre, else seconds) between two spans that still
  // counts as the same physical damage.
  gap: number;
  byMetre: boolean;
}

// [lo, hi] span used for the sweep. With distance data we use the RoI's
// begin/end bounds; otherwise a zero-width point interval at the peak position.
const spanOf = (d: MergeableDetection, byMetre: boolean): [number, number] => {
  const hasSpan =
    byMetre &&
    d.beginDistance !== undefined &&
    d.endDistance !== undefined &&
    !(d.beginDistance === 0 && d.endDistance === 0);
  if (hasSpan) {
    const a = d.beginDistance as number;
    const b = d.endDistance as number;
    return a <= b ? [a, b] : [b, a];
  }
  const p = byMetre ? d.meters : d.timestamp;
  return [p, p];
};

export function mergeDetections<T extends MergeableDetection>(
  items: T[],
  { gap, byMetre }: MergeOptions
): T[] {
  // Group by main code so different codes never merge into each other.
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const bucket = groups.get(item.mainCode);
    if (bucket) bucket.push(item);
    else groups.set(item.mainCode, [item]);
  }

  const merged: { repr: T; lo: number; hi: number }[] = [];

  for (const group of Array.from(groups.values())) {
    // Sort by span start so a single forward sweep can union overlaps.
    const sorted = group
      .map((item) => {
        const [lo, hi] = spanOf(item, byMetre);
        return { item, lo, hi };
      })
      .sort((a, b) => a.lo - b.lo);

    let current: { repr: T; lo: number; hi: number } | null = null;
    for (const { item, lo, hi } of sorted) {
      if (current && lo <= current.hi + gap) {
        // Same physical damage: union the spans...
        current.hi = Math.max(current.hi, hi);
        // ...and keep the strongest detection as the representative.
        if (item.accuracy > current.repr.accuracy) {
          current.repr = { ...item, beginDistance: current.lo, endDistance: current.hi };
        } else {
          current.repr = { ...current.repr, beginDistance: current.lo, endDistance: current.hi };
        }
      } else {
        if (current) merged.push(current);
        current = { repr: { ...item, beginDistance: lo, endDistance: hi }, lo, hi };
      }
    }
    if (current) merged.push(current);
  }

  // Return in position order so tables/pipe read top-to-bottom by distance/time.
  return merged
    .map((m) => m.repr)
    .sort((a, b) => (byMetre ? a.meters - b.meters : a.timestamp - b.timestamp));
}
